class Entry {
  /**
   * @param {String} key
   * @param {Element} element
   * @param {Function} callback
   */
  constructor(key, element, callback) {
    this.key = key;
    this.element = element;
    this.callback = callback;
  }

  /**
   * @param {String} key
   * @param {Element} element
   * @return {Boolean}
   */
  match(key, element) {
    return this.key === key && this.element === element;
  }

  runCallback() {
    this.callback.call(null);
  }
}

/**
 * KeyedVisibilityObserver designed to run callbacks when one of the elements among the group is visible.
 * The group is identified by key.
 *
 * @example
 *   const observer = new KeyedVisibilityObserver({ delay: 0.1 })
 *   // when one of the elements A or B is visible, both callbacks are executed
 *   observer.observe('foo', elementA, () => console.log('executed for elementA'))
 *   observer.observe('foo', elementB, () => console.log('executed for elementB'))
 */
class KeyedVisibilityObserver {
  #observer;

  /**
   * @see {IntersectionObserver}
   * @param {Object} options configuration for IntersectionObserver
   */
  constructor(options = {}) {
    this.options = options;
    this.entries = [];
    this.elementToKey = new Map();
  }

  /**
   * Start to observe an element visibility.
   *
   * @param {String} key group key
   * @param {Element} element HTML element to observe
   * @param {Function} callback function to execute when `isIntersecting` is true
   */
  observe(key, element, callback) {
    if (this.elementToKey.has(element)) {
      return;
    }

    this.elementToKey.set(element, key);
    this.entries.push(new Entry(key, element, callback));
    this.observer.observe(element);
  }

  /**
   * Stop observing for an element visibility.
   *
   * @param {String} key
   * @param {Element} element
   */
  unobserve(key, element) {
    if (!this.elementToKey.delete(element)) {
      return;
    }

    const index = this.entries.findIndex((entry) => entry.match(key, element));
    if (~index) {
      this.entries.splice(index, 1);
    } else {
      // ATM, this case isn't very realistic, so let's log an error if we ever stumble upon such case
      console.error(new Error('Could not find a matching entry!'));
      console.group('Details (key, element)');
      console.log(key);
      console.log(element);
      console.groupEnd();
    }
    this.observer.unobserve(element);
  }

  get observer() {
    return (
      this.#observer ||
      (this.#observer = new IntersectionObserver(this.#resolveObservers.bind(this), this.options))
    );
  }

  #resolveObservers(observerEntries) {
    const keys = new Set();

    for (const { target, isIntersecting } of observerEntries) {
      const key = this.elementToKey.get(target);
      if (isIntersecting && key) {
        keys.add(key);
      }
    }

    // we copy an array here, as callback might modify entries (it's common to immediately unobserve)
    for (const entry of [...this.entries]) {
      keys.has(entry.key) && entry.runCallback();
    }
  }
}

export default KeyedVisibilityObserver;
